
  import { Typo } from '@travelata-libs/ui-kit';
  import { computed, defineComponent, toRefs, unref, type Ref } from 'vue';
  import { useState, useGetters } from 'vuex-composition-helpers';
  import { NAMESPACE as datesTabsStoreName } from '@ve/components/hotel/datesTabs/common/store/DatesTabsStore';
  import { type IMapForDatesTabsGetters } from '@ve/components/hotel/datesTabs/common/types/IMapForDatesTabsStore';
  import { NAMESPACE as hotelPageFiltersStoreName } from '@ve/components/hotel/filters/common/store/hotelPageFiltersAdapterStore';
  import {
    type MapForFiltersGetters,
    type MapForFiltersState,
  } from '@ve/components/hotel/filters/common/store/types';
  import Loader from '@ve/components/loader/common/Loader.vue';
  import { type IMapForSearchFormState } from '@ve/pages/serp/store';
  import { NAMESPACE as searchFromStoreName } from '@ve/pages/serp/store/SearchFormStore';
  import { formatNumber } from '@ve/services/textModifiers';
  import getProductMinPriceByPriority from '../common/services/getProductMinPriceByPriority';
  import {
    getTextDateProduct,
    getTextPriceProduct,
    getTextNightsProduct,
    getTextTourists,
    getDateText,
  } from '../common/services/textHelpers';

  type IGettersDatesTabs = {
    datesAndPrices: Ref<IMapForDatesTabsGetters['datesAndPrices']>;
    selectedDate: Ref<IMapForDatesTabsGetters['selectedDate']>;
  };

  export default defineComponent({
    name: 'HotelMinPriceButtonDesktop',
    components: {
      Loader,
      Typo,
    },
    props: {
      pageMinPrice: {
        type: Number,
        default: null,
      },
    },
    setup(props) {
      const { pageMinPrice } = toRefs(props);

      const { loadingState } = useGetters<MapForFiltersGetters>(
        hotelPageFiltersStoreName,
        ['loadingState'],
      );
      const { productType } = useState<MapForFiltersState>(
        hotelPageFiltersStoreName,
        ['productType'],
      );
      const { touristGroup } = useState<IMapForSearchFormState>(
        searchFromStoreName,
        ['touristGroup'],
      );
      const { datesAndPrices, selectedDate }: IGettersDatesTabs =
        useGetters<IMapForDatesTabsGetters>(datesTabsStoreName, [
          'datesAndPrices',
          'selectedDate',
        ]);

      const productMinPriceByPriority = computed(() =>
        getProductMinPriceByPriority(
          unref(datesAndPrices),
          unref(selectedDate),
        ),
      );

      const priceTextProductByPriority = computed(() =>
        getTextPriceProduct(unref(productMinPriceByPriority)),
      );
      const dateTextProductByPriority = computed(() =>
        getTextDateProduct(unref(productMinPriceByPriority)),
      );
      const nightsTextProductByPriority = computed(() =>
        getTextNightsProduct(unref(productMinPriceByPriority)),
      );

      const dateTextBySelectedDate = computed(() =>
        getDateText(unref(selectedDate)),
      );
      const touristsTextBySearchForm = computed(() =>
        getTextTourists(unref(touristGroup)),
      );

      const isLoadingProductsState = computed(
        () =>
          unref(loadingState) === 'asyncLoading' ||
          unref(loadingState) === 'loading',
      );
      const isDynamicChoiceHasProductsState = computed(
        () =>
          unref(loadingState) === 'finished' &&
          unref(productMinPriceByPriority),
      );
      const isDynamicChoiceWithoutProductsState = computed(
        () =>
          unref(loadingState) === 'finished' &&
          !unref(productMinPriceByPriority),
      );
      const isHotelOfferProduct = computed(
        () => unref(productType) === 'hotelOffer',
      );
      const isStaticPageHasProductsState = computed(() => unref(pageMinPrice));
      const isStaticPageWithoutProductsState = computed(
        () => !unref(pageMinPrice),
      );

      const isChooseRoomTitleVisible = computed(
        () =>
          unref(isHotelOfferProduct) &&
          !unref(isLoadingProductsState) &&
          !unref(isDynamicChoiceWithoutProductsState) &&
          (unref(isDynamicChoiceHasProductsState) ||
            unref(isStaticPageHasProductsState)),
      );

      return {
        isChooseRoomTitleVisible,
        isStaticPageHasProductsState,
        isStaticPageWithoutProductsState,
        isLoadingProductsState,
        isDynamicChoiceHasProductsState,
        isDynamicChoiceWithoutProductsState,

        dateTextProductByPriority,
        priceTextProductByPriority,
        nightsTextProductByPriority,

        dateTextBySelectedDate,
        touristsTextBySearchForm,
        formatNumber,
      };
    },
  });
